import React, { createContext, useContext, useState, useEffect } from "react";

const LanguageContext = createContext({
  language: "english",
  setLanguage: () => {},
  translations: {},
});

const LanguageProvider = ({ children }) => {
  // Load language from localStorage or default to English
  const [language, setLanguage] = useState(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage");
    return savedLanguage || "english";
  });

  // Save language to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("selectedLanguage", language);
  }, [language]);

  // Translation object
  const translations = {
    english: {
      // Settings
      settings: "Settings",
      language: "Language",
      selectLanguage: "Select Language",
      english: "English",
      hindi: "Hindi",
      telugu: "Telugu",
      bengali: "Bengali",
      save: "Save",
      cancel: "Cancel",
      
      // Common
      home: "Home",
      dashboard: "Dashboard",
      games: "Games",
      history: "History",
      ledger: "Ledger",
      rules: "Rules",
      logout: "Logout",
      
      // Aviator
      aviator: "Aviator",
      bet: "Bet",
      cashout: "Cashout",
      balance: "Balance",
      
      // Status
      online: "Online",
      offline: "Offline",
      loading: "Loading...",
      
      // Messages
      success: "Success",
      error: "Error",
      warning: "Warning",
    },
    hindi: {
      // Settings
      settings: "सेटिंग्स",
      language: "भाषा",
      selectLanguage: "भाषा चुनें",
      english: "अंग्रेजी",
      hindi: "हिंदी",
      telugu: "तेलुगु",
      bengali: "बंगाली",
      save: "सेव करें",
      cancel: "रद्द करें",
      
      // Common
      home: "होम",
      dashboard: "डैशबोर्ड",
      games: "गेम्स",
      history: "इतिहास",
      ledger: "लेजर",
      rules: "नियम",
      logout: "लॉगआउट",
      
      // Aviator
      aviator: "एविएटर",
      bet: "बेट",
      cashout: "कैशआउट",
      balance: "बैलेंस",
      
      // Status
      online: "ऑनलाइन",
      offline: "ऑफलाइन",
      loading: "लोड हो रहा है...",
      
      // Messages
      success: "सफलता",
      error: "त्रुटि",
      warning: "चेतावनी",
    },
    telugu: {
      // Settings
      settings: "సెట్టింగ్స్",
      language: "భాష",
      selectLanguage: "భాషను ఎంచుకోండి",
      english: "ఇంగ్లీష్",
      hindi: "హిందీ",
      telugu: "తెలుగు",
      bengali: "బెంగాలీ",
      save: "సేవ్ చేయండి",
      cancel: "రద్దు చేయండి",
      
      // Common
      home: "హోమ్",
      dashboard: "డాష్‌బోర్డ్",
      games: "గేమ్స్",
      history: "చరిత్ర",
      ledger: "లెడ్జర్",
      rules: "నియమాలు",
      logout: "లాగ్‌అవుట్",
      
      // Aviator
      aviator: "ఏవియేటర్",
      bet: "బెట్",
      cashout: "క్యాష్‌అవుట్",
      balance: "బ్యాలెన్స్",
      
      // Status
      online: "ఆన్‌లైన్",
      offline: "ఆఫ్‌లైన్",
      loading: "లోడ్ అవుతోంది...",
      
      // Messages
      success: "విజయం",
      error: "లోపం",
      warning: "హెచ్చరిక",
    },
    bengali: {
      // Settings
      settings: "সেটিংস",
      language: "ভাষা",
      selectLanguage: "ভাষা নির্বাচন করুন",
      english: "ইংরেজি",
      hindi: "হিন্দি",
      telugu: "তেলুগু",
      bengali: "বাংলা",
      save: "সংরক্ষণ করুন",
      cancel: "বাতিল করুন",
      
      // Common
      home: "হোম",
      dashboard: "ড্যাশবোর্ড",
      games: "গেমস",
      history: "ইতিহাস",
      ledger: "লেজার",
      rules: "নিয়ম",
      logout: "লগআউট",
      
      // Aviator
      aviator: "এভিয়েটর",
      bet: "বেট",
      cashout: "ক্যাশআউট",
      balance: "ব্যালেন্স",
      
      // Status
      online: "অনলাইন",
      offline: "অফলাইন",
      loading: "লোড হচ্ছে...",
      
      // Messages
      success: "সফলতা",
      error: "ত্রুটি",
      warning: "সতর্কতা",
    }
  };

  const value = {
    language,
    setLanguage,
    translations: translations[language] || translations.english,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export { LanguageContext, LanguageProvider, useLanguage };
